import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button, Card, Input, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Application detail/edit/create page (`/app-application/:id`, or `/new`). The key
 * field is locked after creation — it is the identifier used in URLs, agent configs and
 * API tokens and changing it would orphan all of those. Connected resources (agents,
 * embed keys, API tokens, connections) are shown in collapsed summary sections so the
 * user can see the application's scope at a glance.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { name: '', key: '', description: '', is_active: true };

function ResourceSection({ title, items, emptyText }) {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="text-xs uppercase text-secondary-500 mb-2">{title} (0)</div>
        <p className="text-xs text-secondary-400">{emptyText}</p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase text-secondary-500 mb-2">{title} ({items.length})</div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item.id} className="inline-flex items-center gap-1 rounded bg-[var(--app-border)]/20 px-2 py-0.5 text-xs">
            {item.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function HeaderSection({ row, isNew, saving, setField, save, remove }) {
  const saveDisabled = saving || !row.name || (isNew && !row.key);
  return (
    <PageHeader
      backTo="/app-application"
      backLabel="Applications"
      title={isNew ? 'New Application' : row.name}
      actions={(
        <Can module="applications" action="govern">
          {!isNew && (
            <>
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={!!row.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
              </label>
              <Button size="sm" variant="secondary" onClick={remove}><Trash2 size={14} className="mr-1 inline" /> Delete</Button>
            </>
          )}
          <Button size="sm" onClick={save} disabled={saveDisabled}>{saving ? 'Saving…' : 'Save'}</Button>
        </Can>
      )}
    />
  );
}

function FormSection({ row, isNew, setField }) {
  return (
    <Card className="p-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Name" value={row.name || ''} onChange={(e) => setField('name', e.target.value)} />
        <Input
          label="Key"
          value={row.key || ''}
          disabled={!isNew}
          placeholder="lowercase-and-dashes"
          onChange={(e) => setField('key', e.target.value)}
        />
      </div>
      <Input label="Description" value={row.description || ''} onChange={(e) => setField('description', e.target.value)} />
      {!isNew && (
        <p className="text-xs text-secondary-500">
          The key is locked after creation — it is the identifier used in agent configs, API tokens and URL paths.
        </p>
      )}
    </Card>
  );
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [row, setRow] = useState(isNew ? { ...BLANK } : null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    fetchApi('applicationShow', id)
      .then((res) => setRow(res))
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setRow((r) => ({ ...r, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const created = await fetchApi('applicationCreate', {
          name: row.name, key: row.key, description: row.description || null,
        });
        nav(`/app-application/${created.id}`, { replace: true });
      } else {
        const updated = await fetchApi('applicationUpdate', {
          id, name: row.name, description: row.description || null, isActive: !!row.is_active,
        });
        setRow((r) => ({ ...r, ...updated, agents: r.agents, embed_keys: r.embed_keys, api_tokens: r.api_tokens, connections: r.connections }));
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete application "${row.name}"? This cannot be undone.`)) return;
    try {
      await fetchApi('applicationDelete', id);
      nav('/app-application');
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  if (loading) return <PageShell><Loader text="Loading application…" /></PageShell>;
  if (!row) return <PageShell><Alert variant="error" title="Could not load">{err || 'Application not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <HeaderSection row={row} isNew={isNew} saving={saving} setField={setField} save={save} remove={remove} />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <FormSection row={row} isNew={isNew} setField={setField} />

      {!isNew && (
        <Card className="p-3 space-y-3">
          <ResourceSection
            title="Agents"
            items={row.agents}
            emptyText="No agents scoped to this application yet."
          />
          <ResourceSection
            title="Embed Keys"
            items={row.embed_keys}
            emptyText="No embed keys scoped to this application yet."
          />
          <ResourceSection
            title="Connections"
            items={row.connections}
            emptyText="No connections scoped to this application yet."
          />
          <ResourceSection
            title="API Tokens"
            items={row.api_tokens}
            emptyText="No API tokens scoped to this application yet."
          />
        </Card>
      )}
    </PageShell>
  );
}
